<template>
  <span class="geo-tracking"></span>
</template>
<script>
import { v4 as uuid } from "uuid";
import Cookies from "js-cookie";
import { getUserLocation } from "~/Scripts/resources/geoTrackingResource";

const hideGeoTrackingCookieName = "559fcc52-7c81-4f34-b611-4768c4b1cb87";

/** Interpolations
 * { $USER_LOC } User's current location friendly name
 * { $ALT_LOC } Alternate site location friendly name
 */
export default {
  name: "GeoTracking",
  props: {
    title: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    confirmText: {
      type: String,
      default: "Yes",
    },
    denyText: {
      type: String,
      default: "No",
    },
    cookieDuration: {
      type: Number,
      default: 7,
    },
  },
  data: () => ( {
    userLocation: undefined,
    cloudFlareCountryCodes: ["XX", "T1", "UNKNOWN"],
  } ),
  computed: {
    instanceId () {
      return uuid();
    },
    isInternationalUser () {
      return !["GB", "IE"].includes( ( this.userLocation || "" ).toUpperCase() );
    },
    isInternationalSite () {
      const lowerLocation = window.location.host.toLowerCase();
      return lowerLocation.includes( "education" ) || lowerLocation.includes( "int" );
    },
    doesUserLocationMatchSite () {
      return this.isInternationalSite === this.isInternationalUser;
    },
    userLocationFriendlyName () {
      return this.isInternationalSite ? "International" : "UK & Ireland";
    },
    alternativeSiteFriendlyName () {
      return this.isInternationalSite ? "UK & Ireland" : "International";
    },
    modalTitle () {
      let result = this.interpolateLocations( this.title );

      if ( this.title ) {
        result = this.title;
      } else {
        result = `You are currently browsing our ${this.userLocationFriendlyName} site.`;
      }
      return result;
    },
    modalDescription () {
      let result = this.interpolateLocations( this.description );

      if ( this.description ) {
        result = this.description.replace( "$ALT_LOC", this.alternativeSiteFriendlyName );
      } else {
        result = `Would you like to switch to our ${this.alternativeSiteFriendlyName} site?`;
      }
      return result;
    },
  },
  watch: {
    userLocation: {
      handler ( value ) {
        if ( this.doesUserLocationMatchSite === false
             && this.cloudFlareCountryCodes.includes( value ) === false
             && this.getUserPreferenceCookie() === false ) {
          this.displayGeoTrackingModal();
        }
      },
    },
  },
  created () {
    getUserLocation().then( ( { data } ) => {
      if ( process.env.NODE_ENV !== "production" ) {
        console.info( "Geo tracking country code: ", data );
      }

      if ( data ) {
        this.userLocation = data;
      } else {
        this.userLocation = "UNKNOWN";
      }
    } );
  },
  methods: {
    getAlternativeSiteDomain () {
      let result;
      const { host } = window.location;

      if ( host.includes( "dev" ) || host.includes( "localhost" ) ) {
        result = this.isInternationalSite ? "https://gl-ecommerce-dev.edcowebsites.co.uk" : "https://gl-ecommerce-dev-int.edcowebsites.co.uk";
      } else if ( host.includes( "staging" ) ) {
        result = this.isInternationalSite ? "https://gl-ecommerce-staging.edcowebsites.co.uk" : "https://gl-ecommerce-staging-int.edcowebsites.co.uk";
      } else {
        result = this.isInternationalSite ? "https://www.gl-assessment.co.uk" : "https://www.gl-education.com";
      }

      return result;
    },
    interpolateLocations ( str ) {
      let result = "";
      if ( str ) {
        result = str.replace( "$USER_LOC", this.userLocationFriendlyName ).replace( "$ALT_LOC", this.alternativeSiteFriendlyName );
      }
      return result;
    },
    getUserPreferenceCookie () {
      return JSON.parse( Cookies.get( hideGeoTrackingCookieName ) || "false" );
    },
    setUserPreferenceCookie () {
      Cookies.set(
        hideGeoTrackingCookieName, JSON.stringify( true ), { expires: this.cookieDuration },
      );
    },
    displayGeoTrackingModal () {
      this.$bvModal.msgBoxConfirm( this.modalDescription, {
        title: this.modalTitle,
        size: "lg",
        buttonSize: "md",
        okTitle: this.confirmText,
        cancelTitle: this.denyText,
        footerClass: "p-2",
        hideHeaderClose: true,
        centered: true,
      } )
        .then( redirect => {
          if ( redirect ) {
            window.location.href = this.getAlternativeSiteDomain();
          } else {
            this.setUserPreferenceCookie();
          }
        } )
        .catch( err => {
          console.error( err );
        } );
    },
  },
};
</script>

<!-- global style (not scoped) -->
<style lang="less">
@import "../Styles/utils/colors.less";

.modal {
  &-content {
    border: none;
    box-shadow: 0 0 5px 0 rgba( 0, 0, 0, 0.2 );
    border-radius: 5px;
  }

  &-header {
    border-top: none;
    background-color: @color-accent-1;

    .close {
      text-shadow: none;
    }
  }

  &-title {
    color: #fff;
  }

  &-footer {
    background-color: #fff;

    button {
        // confirmation button
        &.btn-primary {
          border-color: @color-accent-1;
          background-color: @color-accent-1;
          color: #fff;
          padding: 8px 12px;

          &:hover {
            color: #fff;
            border: 2px solid #fff;
            box-shadow: 0px 2px 5px 2px rgba(0, 0, 0, 0.3);
            padding: 7px 11px;
          }
        }
        // deny button
        &.btn-secondary {
          border: none;
          background-color: transparent;
          color: @color-accent-1;

          &:hover {
            color: #991217;
          }
        }
      }
  }
}

.geo-tracking {
  visibility: hidden;
}
</style>
